import React, { useEffect } from 'react';
import { View, Text } from 'react-native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  withDelay,
} from 'react-native-reanimated';
import { useThemeColors } from '@/lib/useThemeColors';

interface FunnelChartProps {
  stages: { label: string; count: number }[];
  showConversion?: boolean;
}

// Color ramp from deep to light
const BAR_COLORS = [
  '#0D9488', // teal-600
  '#14B8A6', // teal-500
  '#2DD4BF', // teal-400
  '#5EEAD4', // teal-300
  '#99F6E4', // teal-200
];

export function FunnelChart({ stages, showConversion = true }: FunnelChartProps) {
  const themeColors = useThemeColors();

  if (!stages.length) return null;

  const maxCount = stages[0].count || 1;

  // Find biggest drop-off index
  let biggestDropIdx = -1;
  let biggestDrop = 0;
  for (let i = 1; i < stages.length; i++) {
    const prev = stages[i - 1].count;
    const drop = prev > 0 ? ((prev - stages[i].count) / prev) * 100 : 0;
    if (drop > biggestDrop) {
      biggestDrop = drop;
      biggestDropIdx = i;
    }
  }

  return (
    <View className="gap-2">
      {stages.map((stage, idx) => {
        const pct = maxCount > 0 ? (stage.count / maxCount) * 100 : 0;
        const conversion = idx > 0 && stages[idx - 1].count > 0
          ? Math.round((stage.count / stages[idx - 1].count) * 100)
          : null;
        const barColor = BAR_COLORS[Math.min(idx, BAR_COLORS.length - 1)];
        const isBiggestDrop = idx === biggestDropIdx;

        return (
          <View key={`stage-${idx}`}>
            {/* Conversion label between stages */}
            {showConversion && conversion !== null && (
              <View className="flex-row items-center gap-1 mb-1 ml-1">
                <Text
                  className="text-[10px] font-bold"
                  style={{
                    color:
                      conversion >= 70 ? '#22C55E' :
                      conversion >= 40 ? '#F59E0B' :
                      '#EF4444',
                  }}
                >
                  {conversion}% conversion
                </Text>
              </View>
            )}

            <View className="flex-row items-center gap-3">
              {/* Label */}
              <Text
                style={{ color: themeColors.text.secondary, width: 88 }}
                className="text-xs font-medium"
                numberOfLines={1}
              >
                {stage.label}
              </Text>

              {/* Bar */}
              <View className="flex-1 h-7 rounded-md overflow-hidden" style={{ backgroundColor: themeColors.surface.secondary }}>
                <AnimatedBar
                  percentage={pct}
                  color={barColor}
                  index={idx}
                  isBiggestDrop={isBiggestDrop}
                />
              </View>

              {/* Count */}
              <Text
                style={{ color: themeColors.text.primary, width: 40 }}
                className="text-xs font-bold text-right"
              >
                {stage.count}
              </Text>
            </View>
          </View>
        );
      })}
    </View>
  );
}

function AnimatedBar({
  percentage,
  color,
  index,
  isBiggestDrop,
}: {
  percentage: number;
  color: string;
  index: number;
  isBiggestDrop: boolean;
}) {
  const width = useSharedValue(0);

  useEffect(() => {
    width.value = withDelay(
      index * 100,
      withTiming(percentage, { duration: 600 })
    );
  }, [percentage]);

  const animatedStyle = useAnimatedStyle(() => ({
    width: `${width.value}%`,
    height: '100%',
    borderRadius: 6,
    backgroundColor: color,
    borderWidth: isBiggestDrop ? 2 : 0,
    borderColor: isBiggestDrop ? '#EF4444' : 'transparent',
  }));

  return <Animated.View style={animatedStyle} />;
}
